/**
 * pressureAnnotations.ts — نصوص خريطة الضغط: حرفا H وL وقيمهما، وتسميات القيم على
 * خطوط تساوي الضغط. منقولة عن خوارزميات Zoom Earth بثوابتها كما هي.
 *
 * ── لماذا في فضاء الشاشة لا في فضاء الشبكة ──────────────────────────────────
 *
 * كان الاختيار السابق يجري على شبكة النموذج: نافذة بالدرجات، وإسقاط بالدرجات، وسقف
 * عددي عالمي. وذلك يُنتج خللاً لا يُصلَح بمعايرة الأرقام، لأن السؤال نفسه خاطئ: عدد
 * الحروف التي تسع الشاشة ليس خاصّية للأرض بل للشاشة. عند التصغير الشديد تحوي الشاشة
 * نصف الكوكب فيتزاحم مئة حرف؛ وعند التقريب تحوي دولةً واحدة فتبقى خاليةً تماماً رغم
 * أن الحقل نفسه لم يتغيّر.
 *
 * وZoom Earth تقيس كل شيء بالبكسل: عيّنات كل 32 بكسل، إسقاطُ مركزين تقاربا دون 96
 * بكسل، وسقفٌ = مساحة الشاشة ÷ 48400 (‏220² بكسل لكل حرف) محدوداً بين 2 و12. فتبقى
 * كثافة الحروف ثابتةً بالعين عند كل تقريب — وهو ما يُرى في لقطاتها.
 *
 * ── والحقل المقروء هنا هو الحقل المرسوم هناك ────────────────────────────────
 *
 * العيّنة تمرّ بنفس نواة التمويه ثنائية المقياس التي في شيدر الخطوط (نفس الأوزان
 * ونفس الخطوة الهندسية ونفس معامل الحدّة 1.315). وهذا شرط اتّساق لا زخرفة: لو قُرئ
 * الحرف من حقلٍ والخطُّ من حقلٍ آخر لأمكن أن يظهر `L` حيث لا حلقة، أو حلقةٌ بلا حرف.
 * الاشتقاق من مصدر واحد يجعل التطابق **بنيوياً** لا معايرةً تُضبط ثم تنحرف.
 */

import type { Map as MaplibreMap } from 'maplibre-gl';
import type { PressureField } from './hooks/usePressureField';

/** تباعد عيّنات البحث عن المراكز (‏pp عند Zoom Earth). */
const NODE_PX = 32;
/** هامش خارج الشاشة تُبحث فيه المراكز كي لا يقفز حرفٌ إلى الوجود عند حافّة التحريك. */
const MARGIN_X = 96, MARGIN_Y = 48;
/** أقلّ مسافة بين حرفين على الشاشة. */
const CENTER_GAP_PX = 96;
/** بكسل لكل حرف: مساحة الشاشة ÷ هذا = العدد المسموح (‏220²). */
const PX_PER_CENTER = 48400;
const CENTERS_MIN = 2, CENTERS_MAX = 12;
/** تباعد خطوط المسح التي تُبحث عليها تسميات القيم (‏Ap عند Zoom Earth). */
const LABEL_SCAN_PX = 160;
/** خطوة المشي على خطّ المسح. */
const LABEL_STEP_PX = 16;
/*
 * حدّ التصادم بين تسميتين — أرقام Zoom Earth: 56 بكسل حين يتطابق النصّان، و16 حين
 * يختلفان. والتفرقة مقصودة: رقمان متجاوران يقولان «1020» و«1020» تكرارٌ بلا معلومة
 * فيُبعَدان، أمّا «1020» و«1016» متجاوران فيقولان اتجاه التدرّج — وهو أنفع ما تعطيه
 * التسمية أصلاً، فيُسمح بتقاربهما.
 */
const LABEL_GAP_SAME = 56, LABEL_GAP_OTHER = 16;
/** أقصى عدد تسميات في المشهد — سقف أمان لا معيار اختيار. */
const LABEL_MAX = 64;

/**
 * الخطوة عند التقريب z — أرقام Zoom Earth وعتباتها.
 *
 * العتبتان ليستا عددين صحيحين بل `4+√2−1` و`6+√2−1`، فيقع الانتقال في منتصف قفزة
 * التقريب لا على حافّتها: لا تتضاعف كثافة الخطوط في اللحظة التي تُفلت فيها إصبع
 * المستخدم عجلة التصغير.
 */
export function isobarInterval(zoom: number): number {
    const q = Math.SQRT2 - 1;
    return zoom < 4 + q ? 4 : zoom < 6 + q ? 2 : 1;
}

/**
 * ── خطوة نواة التمويه — **المصدر الوحيد للقاعدة** ────────────────────────────
 *
 * الخطوة كسرٌ ثابت من محيط الكرة، لا مقدارٌ يتبع التقريب. واشتقاقها: نصف القطر عند
 * Zoom Earth هو `2^zoom / 10` بكسل، وعرض العالم `512·2^zoom` بكسل — فيختصر الأسّ
 * تماماً وتبقى النسبة `1/5120` مهما بلغ التكبير. وهذا مقصود: خطّ الضغط يجب أن يبقى
 * في موضعه الجغرافي بعينه حين يقرّب المستخدم، لا أن يتموّج ويتبدّل.
 *
 * ولماذا دالّة مُصدَّرة لا رقمٌ في مكانين: النواة تُقيَّم مرّتين في نظامين مختلفين —
 * على الـGPU لرسم الخطّ، وعلى المعالج لاختيار مواضع H/L والتسميات. ولو افترقت
 * الخطوتان لأمكن أن يظهر `L` حيث لا حلقة، أو حلقةٌ بلا حرف. فالقاعدة تُكتب هنا
 * وتُستورد هناك، ويستحيل أن تُعدَّل إحداهما وتُنسى الأخرى.
 */
export const BLUR_STEP_FRACTION = 1 / 5120;

/**
 * مضاعِف الخطوة حسب دقّة النسيج.
 *
 * قياسٌ على نسيج ضغط حقيقي: **63.8% من الخلايا المتجاورة متطابقة بايتاً ببايت** في
 * النسيج الرمادي 8-بت. خطوة تكميمه 0.373 هكتوباسكال، والفرق الحقيقي بين خليّتين في
 * تدرّج معتاد ~0.22 — فيخرج الحقل سلّمَ هضابٍ عرض كلٍّ منها نحو ثلاث خلايا.
 *
 * وأثر ذلك على الخطّ ليس ضوضاءً بل **بقعة**: عرض الخطّ يُقاس بميل الحقل، وميل الهضبة
 * صفر — فإن وقعت قيمتها على مستوى كنتور أُضيء سطحها كلّه. والنواة الافتراضية تمتدّ
 * ±1.1 خليّة فلا تعبر الهضبة أصلاً؛ وبثلاثة أضعاف تمتدّ ±3.3 خليّة فتجسر عليها ويعود
 * الميل متّصلاً. قِيس أثرها: نسبة البكسل الواقع داخل كتلة مصمتة تسقط من 0.63% إلى صفر.
 *
 * والثمن تعميمٌ طفيف لموضع الخطّ — مقبولٌ لأنه بديلٌ عن بقعة، **ولا يُدفع أصلاً** متى
 * توفّر نظير 16-بت: تكميمه 0.0014 هكتوباسكال فلا تنشأ الهضبة، ويعود المضاعِف إلى 1.
 */
export function blurStepScale(p16: boolean): number {
    return p16 ? 1 : 3;
}

// ── نواة التمويه: مطابقة لدالّة isoBlur في شيدر IsobarGLLayer ──────────────────
interface Tap { dx: number; dy: number; w: number; narrow: boolean }
const TAPS: Tap[] = (() => {
    const taps: Tap[] = [];
    for (let dy = -4; dy <= 4; dy++) {
        for (let dx = -4; dx <= 4; dx++) {
            if (!dx && !dy) continue;
            taps.push({ dx, dy, w: Math.exp(-(dx * dx + dy * dy) / 8), narrow: Math.abs(dx) <= 1 && Math.abs(dy) <= 1 });
        }
    }
    return taps;
})();
const CENTER_W = 1;   // exp(0)

/**
 * خطوة النواة بوحدة خلايا الشبكة — ترجمة `BLUR_STEP_FRACTION` إلى فضاء الشبكة.
 *
 * أفقياً: كسرُ المحيط مضروباً في عدد الأعمدة.
 * ورأسياً: يُضرب في cos(lat) لأن مركاتور يمطّ العرض. والاشتقاق من الشيدر: هناك تُقاس
 * الخطوة الرأسية في إحداثيّ `v` الخطّيّ في خطّ العرض، فتُضرب في |dv/dmercY| = 2·cos(lat)
 * — ومع تحويلها إلى صفوف (‏×H) تخرج `H·cos(lat)/2560` وهي نفس ما هنا. فتبقى النواة
 * دائرةً على الأرض لا بيضاويةً متزايدة الاستطالة كلّما ارتفع العرض.
 */
function stepCells(field: PressureField, lat: number) {
    const scale = BLUR_STEP_FRACTION * blurStepScale(field.p16);
    return {
        x: field.width * scale,
        y: field.height * scale * 2 * Math.max(0.15, Math.cos((lat * Math.PI) / 180)),
    };
}

/** قيمة الشبكة بالاستيفاء الثنائي، ملفوفةً بخطّ الطول ومقصوصةً بخطّ العرض. */
function bilinear(field: PressureField, col: number, row: number): number {
    const { values, width, height } = field;
    const c0 = Math.floor(col), r0 = Math.floor(row);
    const fx = col - c0, fy = row - r0;
    const r0c = Math.min(height - 1, Math.max(0, r0));
    const r1c = Math.min(height - 1, Math.max(0, r0 + 1));
    const c0w = ((c0 % width) + width) % width;
    const c1w = ((c0 + 1) % width + width) % width;
    const a = values[r0c * width + c0w], b = values[r0c * width + c1w];
    const c = values[r1c * width + c0w], d = values[r1c * width + c1w];
    return (a + (b - a) * fx) * (1 - fy) + (c + (d - c) * fx) * fy;
}

function colOf(field: PressureField, lon: number) { return ((lon + 180) / 360) * field.width - 0.5; }
function rowOf(field: PressureField, lat: number) { return ((lat + 90) / 180) * field.height - 0.5; }

/** القيمة الخام (بلا تمويه) — الرقم المكتوب تحت الحرف يُقرأ منها. */
export function rawValue(field: PressureField, lon: number, lat: number): number {
    return bilinear(field, colOf(field, lon), rowOf(field, lat));
}

/**
 * الحقل الذي تُرسم منه الخطوط: تمويه ضيّق وعريض، ثم إعادة الفرق بينهما مضروباً في
 * 1.315 حيث يكون كبيراً. تفصيلُ العلّة في تعليق `isoBlur` بالشيدر — والدالّتان يجب
 * أن تبقيا متطابقتين حرفاً بحرف وإلّا افترق الحرف عن حلقته.
 */
export function isoValue(field: PressureField, lon: number, lat: number): number {
    const col = colOf(field, lon), row = rowOf(field, lat);
    const step = stepCells(field, lat);
    const centre = bilinear(field, col, row);
    let nSum = centre * CENTER_W, nW = CENTER_W;
    let bSum = nSum, bW = nW;
    for (const t of TAPS) {
        const v = bilinear(field, col + t.dx * step.x, row + t.dy * step.y) * t.w;
        bSum += v; bW += t.w;
        if (t.narrow) { nSum += v; nW += t.w; }
    }
    const narrow = nSum / nW, broad = bSum / bW;
    const sharp = narrow - broad;
    return broad + sharp * 1.315 * smoothstep(1, 4, Math.abs(sharp));
}

function smoothstep(a: number, b: number, x: number): number {
    const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
    return t * t * (3 - 2 * t);
}

export interface PressureCentre { kind: 'H' | 'L'; lon: number; lat: number; value: number }
export interface IsobarLabel { lon: number; lat: number; text: string; rotate: number }

interface Screen { toLngLat(x: number, y: number): { lng: number; lat: number } }

/**
 * مراكز الضغط داخل المشهد الحالي.
 *
 * @param interval خطوة الكنتور — عتبة البروز تتبعها (‏max(1, interval/2) عند Zoom
 *        Earth). والربط بينهما ليس اعتباطاً: مركزٌ بروزه أقلّ من نصف الخطوة قد لا يقع
 *        بينه وبين محيطه أي مستوى، فيُكتب حرفه ولا تُرسم حوله حلقة — وهو بالضبط
 *        «‏H بلا خطوط» الذي رُصد سابقاً. بربطهما يستحيل ذلك بناءً.
 */
export function findPressureCentres(
    field: PressureField, map: MaplibreMap, width: number, height: number, interval: number,
): PressureCentre[] {
    if (width < 96 || height < 96) return [];
    const screen = map as unknown as Screen & MaplibreMap;
    const cols = Math.ceil((width + MARGIN_X * 2) / NODE_PX) + 1;
    const rows = Math.ceil((height + MARGIN_Y * 2) / NODE_PX) + 1;
    if (cols < 3 || rows < 3) return [];

    // شبكة العيّنات. العقد القطبية القصوى تُترك NaN: ضغطُ سطح البحر فوق هضبة أنتاركتيكا
    // (ثلاثة كيلومترات جليداً) ليس قياساً بل استقراءً إلى مستوى غير موجود، وتدرّجه هائل —
    // فيتصدّر قائمةَ البروز ويلتهم سقف الحروف كلَّه، ولا يبقى للمنظومات الحقيقية مكان.
    const val = new Float32Array(cols * rows);
    for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
            const x = -MARGIN_X + i * NODE_PX, y = -MARGIN_Y + j * NODE_PX;
            const ll = screen.unproject([x, y]);
            val[j * cols + i] = Number.isFinite(ll.lat) && Math.abs(ll.lat) <= 89.5
                ? isoValue(field, ll.lng, ll.lat) : NaN;
        }
    }

    const EPS = 1e-4;
    const minProminence = Math.max(1, interval / 2);
    const found: (PressureCentre & { prominence: number; area: number; x: number; y: number })[] = [];

    for (let j = 1; j < rows - 1; j++) {
        for (let i = 1; i < cols - 1; i++) {
            const k = j * cols + i;
            const v = val[k];
            if (!Number.isFinite(v)) continue;
            let isHigh = true, isLow = true, strictHigh = false, strictLow = false;
            for (let dy = -1; dy <= 1 && (isHigh || isLow); dy++) {
                for (let dx = -1; dx <= 1; dx++) {
                    if (!dx && !dy) continue;
                    const u = val[k + dy * cols + dx];
                    if (!Number.isFinite(u)) { isHigh = isLow = false; break; }
                    isHigh = isHigh && v >= u - EPS;
                    isLow = isLow && v <= u + EPS;
                    strictHigh = strictHigh || v > u + EPS;
                    strictLow = strictLow || v < u - EPS;
                }
            }
            const high = isHigh && strictHigh, low = isLow && strictLow;
            if (!high && !low) continue;

            const kind: 'H' | 'L' = high ? 'H' : 'L';
            const { prominence, area } = prominenceOf(val, cols, rows, k, kind);
            if (prominence < minProminence) continue;

            // تنقية دون العقدة: تسلّق بخطوات 16→1 بكسل نحو القمّة/القاع الحقيقيَّين.
            // بلا هذا يعلق الحرف على أقرب عقدة من شبكة الـ32 بكسل، فيقفز 32 بكسل مع كل
            // تحريك بمقدار بكسل — يرتعش الحرف فوق منظومة ساكنة.
            const refined = refine(field, screen, -MARGIN_X + i * NODE_PX, -MARGIN_Y + j * NODE_PX, kind);
            if (refined.x < 0 || refined.y < 0 || refined.x > width || refined.y > height) continue;

            const value = rawValue(field, refined.lon, refined.lat);
            // سلامة القيمة: منخفضٌ فوق 1030 أو مرتفعٌ دون 990 ليس منظومة بل أثر ترميز.
            if (!Number.isFinite(value) || (kind === 'L' ? value > 1030 : value < 990)) continue;
            found.push({ kind, lon: refined.lon, lat: refined.lat, value, prominence, area, x: refined.x, y: refined.y });
        }
    }

    found.sort((a, b) => b.prominence - a.prominence || b.area - a.area);
    const cap = Math.min(CENTERS_MAX, Math.max(CENTERS_MIN, Math.floor((width * height) / PX_PER_CENTER)));
    const kept: typeof found = [];
    for (const c of found) {
        if (kept.some((k) => Math.hypot(c.x - k.x, c.y - k.y) < CENTER_GAP_PX)) continue;
        kept.push(c);
        if (kept.length === cap) break;
    }
    return kept.map(({ kind, lon, lat, value }) => ({ kind, lon, lat, value: Math.round(value) }));
}

/**
 * بروز القمّة/القاع: كم يمكن أن ننزل عن قيمتها قبل أن تصل المنطقة إلى نقطة أعلى منها.
 *
 * فيضانٌ من العقدة يأخذ في كل خطوة أقربَ جارٍ إلى قيمة المركز؛ فإن ظهر جارٌ يتجاوز
 * المركز توقّفنا — الفرق بين المركز وآخر ما ضُمّ هو البروز. هذا هو التعريف الطبوغرافي
 * المعتاد، وهو ما يفصل منظومةً حقيقية عن تموّجة في منحدر واسع: التموّجة تلامس ما هو
 * أعلى منها بعد نصف هكتوباسكال، والمرتفع الحقيقي ينزل عشرات.
 */
function prominenceOf(
    val: Float32Array, cols: number, rows: number, start: number, kind: 'H' | 'L',
): { prominence: number; area: number } {
    const peak = val[start];
    const sign = kind === 'H' ? 1 : -1;
    const seen = new Uint8Array(cols * rows);
    seen[start] = 1;
    // الحدّ: عدد العقد التي نسمح بمسحها. منظومة ضغط جوي تقاس بآلاف الكيلومترات،
    // لكن السقف يمنع فيضاناً يبتلع نصف الشاشة عند حقل مسطّح تماماً.
    const LIMIT = 4096;
    let frontier: number[] = [start];
    let area = 1;
    let level = peak;
    while (area < LIMIT) {
        let best = -1, bestVal = -Infinity;
        const nextFrontier: number[] = [];
        for (const k of frontier) {
            const i = k % cols, j = (k / cols) | 0;
            for (let dy = -1; dy <= 1; dy++) {
                for (let dx = -1; dx <= 1; dx++) {
                    if (!dx && !dy) continue;
                    const ni = i + dx, nj = j + dy;
                    // الوصول إلى حافّة النافذة = منظومة تمتدّ خارج المشهد؛ نعتبر بروزها
                    // بالغاً لا مجهولاً، وإلّا سقط كل مرتفعٍ يلامس حافّة الشاشة.
                    if (ni <= 0 || nj <= 0 || ni >= cols - 1 || nj >= rows - 1) return { prominence: Infinity, area };
                    const nk = nj * cols + ni;
                    if (seen[nk]) continue;
                    const u = val[nk];
                    if (!Number.isFinite(u)) return { prominence: Infinity, area };
                    if (sign * (u - peak) > 1e-6) return { prominence: sign * (peak - level), area };
                    const score = sign * u;
                    if (score > bestVal) { bestVal = score; best = nk; }
                }
            }
            nextFrontier.push(k);
        }
        if (best < 0) break;
        seen[best] = 1;
        level = val[best];
        frontier = nextFrontier.concat(best);
        area++;
    }
    return { prominence: sign * (peak - level), area };
}

/** تسلّق بخطوات متناقصة نحو القمّة/القاع الحقيقيَّين بدقّة بكسل واحد. */
function refine(
    field: PressureField, screen: Screen & MaplibreMap, x0: number, y0: number, kind: 'H' | 'L',
): { x: number; y: number; lon: number; lat: number } {
    const sign = kind === 'H' ? 1 : -1;
    let x = x0, y = y0;
    let ll = screen.unproject([x, y]);
    let best = isoValue(field, ll.lng, ll.lat);
    for (let step = 16; step >= 1; step /= 2) {
        for (let pass = 0; pass < 2; pass++) {
            let bx = x, by = y, bv = best, bll = ll;
            for (let dy = -1; dy <= 1; dy++) {
                for (let dx = -1; dx <= 1; dx++) {
                    if (!dx && !dy) continue;
                    const nx = x + dx * step, ny = y + dy * step;
                    const nll = screen.unproject([nx, ny]);
                    if (!Number.isFinite(nll.lat) || Math.abs(nll.lat) > 89.5) continue;
                    const v = isoValue(field, nll.lng, nll.lat);
                    if (sign * (v - bv) > 0) { bv = v; bx = nx; by = ny; bll = nll; }
                }
            }
            if (bx === x && by === y) break;
            x = bx; y = by; best = bv; ll = bll;
        }
    }
    return { x, y, lon: ll.lng, lat: ll.lat };
}

/**
 * تسميات القيم على الخطوط.
 *
 * لا تُتَّبع هندسة الخط (لا وجود لها — الخطّ يُرسم بالشيدر). بدلاً منها يُمشى على
 * شبكة خطوط مسح أفقية ورأسية كل 160 بكسل، ويُبحث بين كل عيّنتين متجاورتين عن عبور
 * مستوىً؛ يُنصَّف الموضع أربع مرّات، ثم يُقرأ الميل ليدور النصّ مع الخط لا عبره.
 *
 * وهو حرفياً ما تفعله Zoom Earth، وللسبب نفسه: التسمية بحاجة إلى **نقطة على الخط**
 * لا إلى الخط كلّه، وإيجاد نقطة أرخص بمراتب من تتبّع مضلّع.
 */
export function findIsobarLabels(
    field: PressureField, map: MaplibreMap, width: number, height: number, interval: number,
    centres: PressureCentre[],
): IsobarLabel[] {
    if (width < 96 || height < 96) return [];
    const screen = map as unknown as Screen & MaplibreMap;
    const at = (x: number, y: number) => {
        const ll = screen.unproject([x, y]);
        if (!Number.isFinite(ll.lat) || Math.abs(ll.lat) > 89.5) return null;
        return { lng: ll.lng, lat: ll.lat, v: isoValue(field, ll.lng, ll.lat) };
    };

    const candidates: (IsobarLabel & { x: number; y: number; score: number })[] = [];
    const EDGE = 24;

    const walk = (horizontal: boolean, fixed: number, span: number) => {
        let prev = horizontal ? at(0, fixed) : at(fixed, 0);
        for (let t = LABEL_STEP_PX; t <= span; t += LABEL_STEP_PX) {
            const cur = horizontal ? at(t, fixed) : at(fixed, t);
            if (prev && cur) {
                const lo = Math.min(prev.v, cur.v), hi = Math.max(prev.v, cur.v);
                for (let level = Math.ceil(lo / interval) * interval; level <= hi; level += interval) {
                    // تنصيف على القطعة: أربع مرّات تُنزل الخطأ من 16 بكسل إلى بكسل واحد.
                    let a = t - LABEL_STEP_PX, b = t, va = prev.v;
                    for (let it = 0; it < 4; it++) {
                        const m = (a + b) / 2;
                        const s = horizontal ? at(m, fixed) : at(fixed, m);
                        if (!s) break;
                        if ((va - level) * (s.v - level) <= 0) b = m; else { a = m; va = s.v; }
                    }
                    const px = horizontal ? (a + b) / 2 : fixed;
                    const py = horizontal ? fixed : (a + b) / 2;
                    if (px < EDGE || py < EDGE || px > width - EDGE || py > height - EDGE) continue;
                    // الميل بفرق مركزي ±2 بكسل؛ الخطّ عموديّ عليه، فدوران النصّ هو
                    // زاوية الميل نفسها (‏atan2 مقلوبة) — فيقرأ النصّ على امتداد الخطّ.
                    const gx = (at(px + 2, py)?.v ?? NaN) - (at(px - 2, py)?.v ?? NaN);
                    const gy = (at(px, py + 2)?.v ?? NaN) - (at(px, py - 2)?.v ?? NaN);
                    if (!Number.isFinite(gx) || !Number.isFinite(gy)) continue;
                    const mag = Math.hypot(gx, gy);
                    if (mag < 1e-6) continue;
                    let rotate = (Math.atan2(gx, -gy) * 180) / Math.PI;
                    // النصّ لا يُقرأ مقلوباً: نُبقيه في نصف الدائرة العلوي دائماً.
                    if (rotate > 90) rotate -= 180; else if (rotate < -90) rotate += 180;
                    const ll = screen.unproject([px, py]);
                    // تفضيل المواضع التي يعبر فيها الخطّ خطَّ المسح عمودياً: هناك تكون
                    // التسمية أبعد ما تكون عن جارتيها على نفس الخط.
                    const along = horizontal ? Math.abs(gx) / mag : Math.abs(gy) / mag;
                    candidates.push({
                        lon: ll.lng, lat: ll.lat, text: String(Math.round(level)),
                        rotate, x: px, y: py, score: along,
                    });
                }
            }
            prev = cur;
        }
    };

    for (let y = LABEL_SCAN_PX / 2; y < height; y += LABEL_SCAN_PX) walk(true, y, width);
    for (let x = LABEL_SCAN_PX / 2; x < width; x += LABEL_SCAN_PX) walk(false, x, height);

    // إسقاط ما يقع على حرف H/L، ثم تصفية التصادم بالأفضل أوّلاً.
    const centrePx = centres.map((c) => map.project([c.lon, c.lat]));
    candidates.sort((a, b) => b.score - a.score);
    const kept: typeof candidates = [];
    for (const c of candidates) {
        if (centrePx.some((p) => Math.abs(c.x - p.x) < 40 && Math.abs(c.y - p.y) < 32)) continue;
        if (kept.some((k) => Math.hypot(c.x - k.x, c.y - k.y) < (k.text === c.text ? LABEL_GAP_SAME : LABEL_GAP_OTHER))) continue;
        kept.push(c);
        if (kept.length >= LABEL_MAX) break;
    }
    return kept.map(({ lon, lat, text, rotate }) => ({ lon, lat, text, rotate }));
}
